import { Controller } from "@hotwired/stimulus";
import { createMap, googleMapsUrl, loadMapLibre, toLngLat } from "src/maps";

// South-west and north-east corners. Fitting the map to them frames the whole
// country whatever the container's size.
const BRAZIL = [{ lat: -33.8, lng: -74.0 }, { lat: 5.3, lng: -34.8 }];

// Point colours by event group (MapsController::EVENT_KIND_GROUPS), in the
// colours of the Google map's pins: red, blue, yellow, green and pink.
const CATEGORY_COLORS = {
  theft: "#e53935",
  accident: "#1e88e5",
  fire: "#fbc02d",
  damage: "#43a047",
};
const OTHER_COLOR = "#d81b60";

// Cluster sizes and colours step up at 10 and 100 points, as the Google
// clusterer's did.
const CLUSTER_STEPS = { color: ["#51bbd6", 10, "#f1c40f", 100, "#e74c3c"], radius: [16, 10, 20, 100, 26] };

export default class extends Controller {
  static values = { locations: Array }

  async connect() {
    // Stimulus parses the attribute on every read, so it is read once.
    this.locations = this.locationsValue;

    const map = await createMap(this.element, { bounds: BRAZIL });
    if (!map) return;
    if (!this.element.isConnected) {
      map.remove();
      return;
    }

    this.map = map;
    this.Popup = (await loadMapLibre()).Popup;
    this.addLayers();
    this.bindEvents();
  }

  disconnect() {
    this.map?.remove();
    this.map = null;
  }

  addLayers() {
    this.map.addSource("sinisters", {
      type: "geojson",
      // Features carry only what the layers style by. The popup reads the rest
      // from this.locations, which keeps null and nested values intact.
      data: {
        type: "FeatureCollection",
        features: this.locations.map((location, index) => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: toLngLat(location) },
          properties: { index, category: location.category },
        })),
      },
      cluster: true,
      // MapLibre's zoom levels run one below Google's, whose clusterer stopped
      // at 9.
      clusterMaxZoom: 8,
    });

    this.map.addLayer({
      id: "clusters",
      type: "circle",
      source: "sinisters",
      filter: ["has", "point_count"],
      paint: {
        "circle-color": ["step", ["get", "point_count"], ...CLUSTER_STEPS.color],
        "circle-radius": ["step", ["get", "point_count"], ...CLUSTER_STEPS.radius],
        "circle-stroke-width": 3,
        "circle-stroke-color": "rgba(255, 255, 255, 0.6)",
      },
    });

    this.map.addLayer({
      id: "cluster-counts",
      type: "symbol",
      source: "sinisters",
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        // Must be a font the style's glyph server has; OpenFreeMap serves Noto
        // Sans. Another MAP_STYLE_URL provider may need a different name here.
        "text-font": ["Noto Sans Bold"],
        "text-size": 12,
      },
    });

    this.map.addLayer({
      id: "sinisters",
      type: "circle",
      source: "sinisters",
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-color": ["match", ["get", "category"], ...Object.entries(CATEGORY_COLORS).flat(), OTHER_COLOR],
        "circle-radius": 7,
        "circle-stroke-width": 2,
        "circle-stroke-color": "#ffffff",
      },
    });
  }

  bindEvents() {
    this.map.on("click", "clusters", async (event) => {
      const [cluster] = event.features;
      const zoom = await this.map.getSource("sinisters").getClusterExpansionZoom(cluster.properties.cluster_id);
      this.map.easeTo({ center: cluster.geometry.coordinates, zoom });
    });

    this.map.on("click", "sinisters", (event) => {
      const [feature] = event.features;
      const location = this.locations[feature.properties.index];

      new this.Popup({ maxWidth: "300px" })
        .setLngLat(feature.geometry.coordinates)
        .setDOMContent(this.popupContent(location))
        .addTo(this.map);
    });

    for (const layer of ["clusters", "sinisters"]) {
      this.map.on("mouseenter", layer, () => { this.map.getCanvas().style.cursor = "pointer"; });
      this.map.on("mouseleave", layer, () => { this.map.getCanvas().style.cursor = ""; });
    }
  }

  // Built from text nodes, never an HTML string: client and insurer names are
  // user data.
  popupContent(location) {
    const content = document.createElement("div");

    const heading = document.createElement("h4");
    heading.textContent = `Nº: ${location.number}`;
    content.append(heading);

    const lines = [["Tipo de Evento", location.event_kind]];
    if (location.category === "damage") lines.push(["Tipo de Avaria", location.damage_kind ?? ""]);
    lines.push(["Apólice", location.policy_number], ["Cliente", location.client], ["Seguradora", location.insurer]);

    for (const [label, value] of lines) {
      const line = document.createElement("p");
      line.textContent = `${label}: ${value}`;
      content.append(line);
    }

    content.append(
      paragraphWithLink("Mais Detalhes", location.url),
      paragraphWithLink("Abrir no Google Maps", googleMapsUrl(location), { newTab: true })
    );

    return content;
  }
}

function paragraphWithLink(text, href, { newTab = false } = {}) {
  const link = document.createElement("a");
  link.textContent = text;
  link.href = href;
  if (newTab) {
    link.target = "_blank";
    link.rel = "noopener";
  }

  const paragraph = document.createElement("p");
  paragraph.append(link);
  return paragraph;
}
